import type { App } from 'vue'

import { setupLayouts } from 'virtual:generated-layouts'
import type { RouteRecordRaw } from 'vue-router/auto'
// eslint-disable-next-line import/no-unresolved
import { createRouter, createWebHistory } from 'vue-router/auto'

import { redirects, routes } from './additional-routes'
import { setupGuards } from './guards'

function recursiveLayouts(route: RouteRecordRaw): RouteRecordRaw {
  if (route.children) {
    for (let i = 0; i < route.children.length; i++)
      route.children[i] = recursiveLayouts(route.children[i])

    return route
  }

  return setupLayouts([route])[0]
}

// eslint-disable-next-line @typescript-eslint/no-shadow
function flattenRoutes(routes: any[], parentPath = ''): any {
  let flattenedRoutes = []

  for (const route of routes) {
    const { path, children, ...rest } = route

    // Add the parent path as a prefix to the current route's path
    const fullPath = `${parentPath}${path}`

    // Create a new route object with the modified path
    const flattenedRoute = { ...rest, path: fullPath }

    flattenedRoutes.push(flattenedRoute)

    // Recursively flatten children if present
    if (children && children.length > 0)
      flattenedRoutes = flattenedRoutes.concat(flattenRoutes(children, fullPath))
  }

  return flattenedRoutes
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition)
      return savedPosition
    if (to.hash)
      return { el: to.hash, behavior: 'smooth', top: 60 }
    if (to.path === from.path)
      return false

    return { top: 0 }
  },
  extendRoutes: pages => [
    ...redirects,
    ...[
      ...flattenRoutes(routes),
      ...pages,
    ].map(route => recursiveLayouts(route)),
  ],
})

setupGuards(router)

export { router }

export default function (app: App) {
  app.use(router)
}
